'use client';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  FamilyDemoState,
  buildInitialDemoState,
  Notification,
  NotificationStatus,
  CallRecord,
  Message,
  AgendaEvent,
  ActivityRecord,
  ActivityStatus,
  FamilyContact,
  formatDurationLabel,
} from './familyDemoData';

// ─── Storage key & schema version ────────────────────────────────────────────

const STORAGE_KEY = 'lucat-connect-family-demo-v1';
const SCHEMA_VERSION = 1;

// ─── Persistence helpers ──────────────────────────────────────────────────────

function loadFromStorage(): FamilyDemoState {
  if (typeof window === 'undefined') return buildInitialDemoState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildInitialDemoState();
    const parsed = JSON.parse(raw) as FamilyDemoState;
    if (parsed.schemaVersion !== SCHEMA_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return buildInitialDemoState();
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return buildInitialDemoState();
  }
}

function saveToStorage(state: FamilyDemoState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

// ─── Context type ─────────────────────────────────────────────────────────────

interface FamilyDemoContextValue {
  state: FamilyDemoState;
  resetFamilyDemoState: () => void;
  addCallRecord: (record: CallRecord) => void;
  updateCallRecord: (id: string, updates: Partial<CallRecord>) => void;
  addMessage: (message: Message) => void;
  markMessageAsRead: (messageId: string) => void;
  handleNotification: (notifId: string, newStatus: NotificationStatus) => void;
  deferNotification: (notifId: string) => void;
  addAgendaEvent: (event: AgendaEvent) => void;
  updateAgendaEvent: (id: string, updates: Partial<AgendaEvent>) => void;
  deleteAgendaEvent: (id: string) => void;
  addActivity: (activity: ActivityRecord) => void;
  updateActivityStatus: (id: string, status: ActivityStatus) => void;
  updateActivityByNotifId: (notifId: string, updates: Partial<ActivityRecord>) => void;
  updateFamilyContact: (id: string, updates: Partial<FamilyContact>) => void;
  removeFamilyContact: (id: string) => void;
  addFamilyContact: (contact: FamilyContact) => void;
  getUnreadAlertCount: () => number;
  getUnreadMessageCount: () => number;
}

const FamilyDemoContext = createContext<FamilyDemoContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function FamilyDemoProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FamilyDemoState>(() => buildInitialDemoState());
  const hydrated = useRef(false);

  // Hydrate from localStorage after mount (avoids SSR mismatch)
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      const stored = loadFromStorage();
      setState(stored);
    }
  }, []);

  // Persist on every state change
  useEffect(() => {
    if (hydrated.current) {
      saveToStorage(state);
    }
  }, [state]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const resetFamilyDemoState = useCallback(() => {
    const fresh = buildInitialDemoState();
    fresh.lastResetAt = new Date().toISOString();
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    setState(fresh);
  }, []);

  const addCallRecord = useCallback((record: CallRecord) => {
    setState((prev) => {
      if (prev.callRecords.some((c) => c.id === record.id)) return prev;
      const next = { ...prev, callRecords: [record, ...prev.callRecords] };
      // Update seniorProfile last call if completed
      if (record.status === 'completed' && record.durationSeconds) {
        next.seniorProfile = {
          ...prev.seniorProfile,
          lastCallAt: record.startedAt,
          lastCallDuration: formatDurationLabel(record.durationSeconds),
        };
      }
      return next;
    });
  }, []);

  const updateCallRecord = useCallback((id: string, updates: Partial<CallRecord>) => {
    setState((prev) => ({
      ...prev,
      callRecords: prev.callRecords.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    }));
  }, []);

  const addMessage = useCallback((message: Message) => {
    setState((prev) => {
      if (prev.messages.some((m) => m.id === message.id)) return prev;
      const msgs = [...prev.messages, message];
      // Update conversation
      const convId = message.conversationId;
      const convs = prev.conversations.map((c) => {
        if (c.id !== convId) return c;
        return {
          ...c,
          messageIds: [...c.messageIds, message.id],
          lastMessageAt: message.createdAt,
          unreadCount:
            message.direction === 'incoming' ? c.unreadCount + 1 : c.unreadCount,
        };
      });
      return { ...prev, messages: msgs, conversations: convs };
    });
  }, []);

  const markMessageAsRead = useCallback((messageId: string) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) =>
        m.id === messageId ? { ...m, status: 'read', readAt: new Date().toISOString() } : m
      ),
    }));
  }, []);

  const handleNotification = useCallback(
    (notifId: string, newStatus: NotificationStatus) => {
      setState((prev) => {
        const now = new Date().toISOString();
        const notifications = prev.notifications.map((n) => {
          if (n.id !== notifId) return n;
          const updated: Notification = { ...n, status: newStatus };
          if (newStatus === 'read' && !n.readAt) updated.readAt = now;
          if (newStatus === 'handled') {
            updated.readAt = updated.readAt ?? now;
            updated.handledAt = now;
          }
          if (newStatus === 'deferred') updated.deferredAt = now;
          return updated;
        });

        // Sync callback activity
        const activities = prev.activities.map((a) => {
          if (a.category !== 'callback' || a.relatedEntityId !== notifId) return a;
          const statusMap: Record<NotificationStatus, ActivityStatus> = {
            unread: 'new',
            read: 'pending',
            deferred: 'deferred',
            handled: 'handled',
          };
          return { ...a, status: statusMap[newStatus] ?? a.status };
        });

        // Sync callback message
        const messages = prev.messages.map((m) => {
          if (m.notifId !== notifId) return m;
          if (newStatus === 'handled') return { ...m, readStatus: 'handled', status: 'handled' as const };
          return m;
        });

        return { ...prev, notifications, activities, messages };
      });
    },
    []
  );

  const deferNotification = useCallback((notifId: string) => {
    handleNotification(notifId, 'deferred');
  }, [handleNotification]);

  const addAgendaEvent = useCallback((event: AgendaEvent) => {
    setState((prev) => {
      if (prev.agendaEvents.some((e) => e.id === event.id)) return prev;
      return { ...prev, agendaEvents: [...prev.agendaEvents, event] };
    });
  }, []);

  const updateAgendaEvent = useCallback((id: string, updates: Partial<AgendaEvent>) => {
    setState((prev) => ({
      ...prev,
      agendaEvents: prev.agendaEvents.map((e) =>
        e.id === id ? { ...e, ...updates } : e
      ),
    }));
  }, []);

  const deleteAgendaEvent = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      agendaEvents: prev.agendaEvents.filter((e) => e.id !== id),
    }));
  }, []);

  const addActivity = useCallback((activity: ActivityRecord) => {
    setState((prev) => {
      if (prev.activities.some((a) => a.id === activity.id)) return prev;
      return { ...prev, activities: [activity, ...prev.activities] };
    });
  }, []);

  const updateActivityStatus = useCallback((id: string, status: ActivityStatus) => {
    setState((prev) => ({
      ...prev,
      activities: prev.activities.map((a) =>
        a.id === id ? { ...a, status } : a
      ),
    }));
  }, []);

  const updateActivityByNotifId = useCallback(
    (notifId: string, updates: Partial<ActivityRecord>) => {
      setState((prev) => ({
        ...prev,
        activities: prev.activities.map((a) => {
          if (a.category !== 'callback' || a.relatedEntityId !== notifId) return a;
          return { ...a, ...updates };
        }),
      }));
    },
    []
  );

  const updateFamilyContact = useCallback(
    (id: string, updates: Partial<FamilyContact>) => {
      setState((prev) => ({
        ...prev,
        familyContacts: prev.familyContacts.map((c) =>
          c.id === id ? { ...c, ...updates } : c
        ),
      }));
    },
    []
  );

  const removeFamilyContact = useCallback((id: string) => {
    setState((prev) => {
      const contact = prev.familyContacts.find((c) => c.id === id);
      if (!contact || contact.isAdmin) return prev;
      return {
        ...prev,
        familyContacts: prev.familyContacts.filter((c) => c.id !== id),
      };
    });
  }, []);

  const addFamilyContact = useCallback((contact: FamilyContact) => {
    setState((prev) => {
      if (prev.familyContacts.some((c) => c.id === contact.id)) return prev;
      return { ...prev, familyContacts: [...prev.familyContacts, contact] };
    });
  }, []);

  const getUnreadAlertCount = useCallback(() => {
    return state.notifications.filter(
      (n) => n.status === 'unread' || n.status === 'deferred'
    ).length;
  }, [state.notifications]);

  const getUnreadMessageCount = useCallback(() => {
    return state.messages.filter(
      (m) => m.direction === 'incoming' && m.readStatus !== 'seen' && m.readStatus !== 'handled' && m.status !== 'read'
    ).length;
  }, [state.messages]);

  const value: FamilyDemoContextValue = {
    state,
    resetFamilyDemoState,
    addCallRecord,
    updateCallRecord,
    addMessage,
    markMessageAsRead,
    handleNotification,
    deferNotification,
    addAgendaEvent,
    updateAgendaEvent,
    deleteAgendaEvent,
    addActivity,
    updateActivityStatus,
    updateActivityByNotifId,
    updateFamilyContact,
    removeFamilyContact,
    addFamilyContact,
    getUnreadAlertCount,
    getUnreadMessageCount,
  };

  return (
    <FamilyDemoContext.Provider value={value}>
      {children}
    </FamilyDemoContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFamilyDemo(): FamilyDemoContextValue {
  const ctx = useContext(FamilyDemoContext);
  if (!ctx) {
    throw new Error('useFamilyDemo must be used within FamilyDemoProvider');
  }
  return ctx;
}
